import classNames from 'classnames';
import React from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Tag from '@/components/molecules/tag/tag';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { HubspotFormData } from '../room-matrix';
import styles from './room-matrix-price.module.scss';

export interface RoomMatrixPriceProps {
  /** The additional tag to display */
  additionalTag?: Content.Components['AdditionalTagDto'];
  /** The amount discounted */
  discount: number | null;
  /** Indicates whether to show hubspot form for inquiries */
  enableHubspotform?: boolean;
  /** The info to pass to the hubspot */
  hubspotInfo?: HubspotFormData;
  /** Whether the card is active */
  isActive?: boolean;
  /** Whether to display the loading state */
  isLoading?: boolean;
  /** The regular price */
  normalPrice: number | null;
  /** An event that is triggered on click */
  onClick?: (
    event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  /** An event triggered when clicking on an inquiry */
  onClickInquiry?: (value?: HubspotFormData) => void;
  /** The total price */
  totalPrice: number | null;
  /** The link to the offer */
  url?: string | null;
}

const additionalTagVariants: Record<
  Content.Components['AdditionalTagTheme'],
  string
> = {
  blackweek: styles.isBlackWeek,
  primary: styles.isPrimary,
};

const RoomMatrixPrice = ({
  additionalTag,
  discount,
  enableHubspotform = false,
  hubspotInfo,
  isActive = false,
  isLoading = false,
  normalPrice,
  onClick,
  onClickInquiry,
  totalPrice,
  url,
}: RoomMatrixPriceProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const isSoldOut = !normalPrice && !totalPrice && !discount;
  const hasAdditionalTag = !!additionalTag;
  const additionalTagVariant = additionalTag?.theme;

  if (isLoading) {
    return (
      <div className={classNames(styles.component, styles.isLoading)}>
        <SkeletonLoader className={styles.loader} height="20px" width="70%" />
        <SkeletonLoader className={styles.loader} height="16px" width="45%" />
      </div>
    );
  }

  const content = (
    <React.Fragment>
      {hasAdditionalTag && (
        <div
          className={classNames(
            styles.additionalWrapper,
            additionalTagVariant && additionalTagVariants[additionalTagVariant],
          )}
        >
          <Typography className={styles.additionalTag} tagStyle="bodySmallBold">
            {additionalTag?.text}
          </Typography>
        </div>
      )}

      {isSoldOut && (
        <Typography tagStyle="priceSmall">
          {t('items.common.words.soldOut')}
        </Typography>
      )}

      {!!discount && (
        <Tag
          text={`${t('items.search.productCard.save')} ${currencyFormatter(discount)}`}
          variant="tertiary"
        />
      )}

      {!!discount && normalPrice && (
        <Typography className={styles.oldPrice} tagStyle="bodySmall">
          {currencyFormatter(normalPrice)}
        </Typography>
      )}

      {totalPrice && (
        <Typography className={styles.price} tagStyle="priceSmall">
          {currencyFormatter(totalPrice)}
        </Typography>
      )}
    </React.Fragment>
  );

  if (isSoldOut && enableHubspotform) {
    return (
      <button
        className={classNames(styles.component, {
          [styles.isActive]: isActive,
        })}
        onClick={() => onClickInquiry?.(hubspotInfo)}
      >
        {content}

        {enableHubspotform && (
          <Typography className={styles.inquire} tagStyle="bodyMedium">
            {t('items.common.words.inquire')}
          </Typography>
        )}
      </button>
    );
  }

  if (isActive || !url || isSoldOut) {
    return (
      <div
        className={classNames(styles.component, {
          [styles.isActive]: isActive,
          [styles.isDisabled]: isSoldOut,
          [styles.hasAdditionalTag]: hasAdditionalTag,
        })}
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      className={classNames(styles.component, {
        [styles.hasAdditionalTag]: hasAdditionalTag,
      })}
      {...(onClick && { onClick })}
      replace={true}
      scroll={false}
      target="_self"
      url={url}
    >
      {content}
    </Link>
  );
};

RoomMatrixPrice.displayName = 'RoomMatrixPrice';

export default RoomMatrixPrice;
